import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  getDoc,
  setDoc,
  where
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { 
  Plus, 
  Trash2, 
  Edit2,
  Check,
  X,
  Loader2, 
  Settings as SettingsIcon, 
  List, 
  Briefcase, 
  Truck, 
  AlertCircle,
  Cloud,
  Save,
  Shield,
  CheckCircle2,
  Database,
  RefreshCw,
  RotateCcw,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { updateDoc } from 'firebase/firestore';
import { getRemoteBackupUrl, updateRemoteBackupUrl } from '../services/backupService';
import { clearDataCache } from '../services/dataCacheService';
import { 
  DEFAULT_ABSENCE_REASONS, 
  DEFAULT_STAFF_ROLES, 
  seedDefaultAbsenceReasons, 
  seedDefaultStaffRoles 
} from '../services/absenceReasonService';

interface SettingItem {
  id: string;
  name: string;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'reasons' | 'roles' | 'backup' | 'system' | 'cache'>('reasons');
  const [items, setItems] = useState<SettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItemName, setNewItemName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [savingSystem, setSavingSystem] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [fixingNurses, setFixingNurses] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const collections = {
    reasons: 'absenceReasons',
    roles: 'staffRoles'
  };

  useEffect(() => {
    setStatusMsg(null);
    if (activeTab === 'backup') {
      fetchRemoteUrl();
    } else if (activeTab === 'system') {
      fetchSystemSettings();
    } else {
      fetchItems();
    }
  }, [activeTab]);

  const fetchSystemSettings = async () => {
    setLoading(true);
    try {
      const settingsRef = doc(db, 'systemSettings', 'general');
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        setMaintenanceMode(settingsSnap.data().maintenanceMode || false);
      }
    } catch (e) {
      console.error("Error fetching system settings:", e);
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    setSavingSystem(true);
    try {
      const settingsRef = doc(db, 'systemSettings', 'general');
      await setDoc(settingsRef, { maintenanceMode: !maintenanceMode }, { merge: true });
      setMaintenanceMode(!maintenanceMode);
    } catch (e) {
      console.error("Error toggling maintenance mode:", e);
    } finally {
      setSavingSystem(false);
    }
  };

  const fetchRemoteUrl = async () => {
    setLoading(true);
    const url = await getRemoteBackupUrl();
    setRemoteUrl(url);
    setLoading(false);
  };

  const handleSaveRemoteUrl = async () => {
    setSavingUrl(true);
    await updateRemoteBackupUrl(remoteUrl);
    setSavingUrl(false);
    alert('הגדרות הגיבוי נשמרו בהצלחה');
  };

  const fetchItems = async () => {
    setLoading(true);
    setConfirmDeleteId(null);
    try {
      const colName = collections[activeTab as 'reasons' | 'roles'];
      const q = query(collection(db, colName), orderBy('name'));
      let snapshot;
      try {
        snapshot = await getDocs(q);
      } catch (err) {
        // Fallback to query without orderBy if index or field issue
        console.warn(`Query with orderBy failed for ${colName}, falling back to plain collection get:`, err);
        snapshot = await getDocs(collection(db, colName));
      }

      let data = snapshot.docs.map(doc => {
        const d = doc.data();
        const extractedName = (d.name || d.reason || d.role || d.title || d.label || '').trim();
        return {
          id: doc.id,
          name: extractedName
        };
      }).filter(item => item.name.length > 0) as SettingItem[];

      // If Firestore collection has 0 valid items, automatically seed the standard default items!
      if (data.length === 0) {
        if (activeTab === 'reasons') {
          await seedDefaultAbsenceReasons();
          const refreshedSnap = await getDocs(collection(db, 'absenceReasons'));
          data = refreshedSnap.docs
            .map(doc => ({ id: doc.id, name: (doc.data().name || doc.data().reason || '').trim() }))
            .filter(i => i.name.length > 0);
          
          if (data.length === 0) {
            // Immediate in-memory fallback so user is NEVER left with an empty screen
            data = DEFAULT_ABSENCE_REASONS.map((r, i) => ({ id: `default-${i}`, name: r }));
          }
          setStatusMsg({ text: 'סיבות ההיעדרות שוחזרו ואותחלו עם ערכי ברירת המחדל התקניים.', type: 'info' });
        } else if (activeTab === 'roles') {
          await seedDefaultStaffRoles();
          const refreshedSnap = await getDocs(collection(db, 'staffRoles'));
          data = refreshedSnap.docs
            .map(doc => ({ id: doc.id, name: (doc.data().name || doc.data().role || '').trim() }))
            .filter(i => i.name.length > 0);
          
          if (data.length === 0) {
            data = DEFAULT_STAFF_ROLES.map((r, i) => ({ id: `default-${i}`, name: r }));
          }
          setStatusMsg({ text: 'תפקידי הצוות שוחזרו ואותחלו עם ערכי ברירת המחדל התקניים.', type: 'info' });
        }
      }

      // Sort alphabetically by Hebrew name
      data.sort((a, b) => a.name.localeCompare(b.name, 'he'));
      setItems(data);
    } catch (error) {
      console.warn(`Could not fetch ${collections[activeTab as 'reasons' | 'roles']} from Firestore:`, error);
      // Fallback so user NEVER sees an empty screen
      if (activeTab === 'reasons') {
        setItems(DEFAULT_ABSENCE_REASONS.map((r, i) => ({ id: `fallback-${i}`, name: r })));
        setStatusMsg({ text: 'מוצגת רשימת ברירת המחדל התקנית של סיבות ההיעדרות.', type: 'info' });
      } else if (activeTab === 'roles') {
        setItems(DEFAULT_STAFF_ROLES.map((r, i) => ({ id: `fallback-${i}`, name: r })));
        setStatusMsg({ text: 'מוצגת רשימת ברירת המחדל התקנית של תפקידי הצוות.', type: 'info' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDefaults = async () => {
    setActionLoading(true);
    setStatusMsg(null);
    setConfirmDeleteId(null);
    try {
      if (activeTab === 'reasons') {
        const count = await seedDefaultAbsenceReasons();
        clearDataCache();
        await fetchItems();
        setStatusMsg({ text: `שוחזרו ונשמרו בהצלחה ${count > 0 ? count : 'כל'} סיבות ההיעדרות התקניות!`, type: 'success' });
      } else if (activeTab === 'roles') {
        const count = await seedDefaultStaffRoles();
        clearDataCache();
        await fetchItems();
        setStatusMsg({ text: `שוחזרו ונשמרו בהצלחה ${count > 0 ? count : 'כל'} תפקידי הצוות התקניים!`, type: 'success' });
      }
    } catch (err) {
      console.error('Error restoring defaults:', err);
      setStatusMsg({ text: 'שגיאה בשחזור ערכי ברירת המחדל.', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setStatusMsg(null), 5000);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    setActionLoading(true);
    try {
      await addDoc(collection(db, collections[activeTab]), {
        name: newItemName.trim(),
        createdAt: new Date().toISOString()
      });
      clearDataCache();
      setNewItemName('');
      fetchItems();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, collections[activeTab]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateItem = async (id: string) => {
    const name = editName.trim();
    if (!name) return;

    setActionLoading(true);
    try {
      await updateDoc(doc(db, collections[activeTab], id), {
        name: name
      });
      clearDataCache();
      setEditingId(null);
      fetchItems();
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, collections[activeTab]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    setActionLoading(true);
    setConfirmDeleteId(null);
    try {
      await deleteDoc(doc(db, collections[activeTab], id));
      clearDataCache();
      fetchItems();
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, collections[activeTab]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearCache = () => {
    setClearingCache(true);
    clearDataCache();
    setTimeout(() => {
      setClearingCache(false);
      alert('הזיכרון המטמון נוקה בהצלחה. המערכת תטען נתונים חדשים מהשרת.');
      window.location.reload();
    }, 1000);
  };

  const handleFixNurses = async () => {
    setFixingNurses(true);
    try {
      const q = query(collection(db, 'staff'), where('role', '==', 'אחות'), where('type', '==', 'assistant'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        alert('לא נמצאו אחיות ברשימת הסייעות.');
        return;
      }

      const promises = snapshot.docs.map(d => updateDoc(doc(db, 'staff', d.id), { type: 'educational' }));
      await Promise.all(promises);
      
      clearDataCache();
      alert(`הועברו ${snapshot.docs.length} אחיות לרשימת הצוות החינוכי בהצלחה!`);
      window.location.reload();
    } catch (error) {
      console.error("Error fixing nurses:", error);
      alert('שגיאה בהעברת האחיות.');
    } finally {
      setFixingNurses(false);
    }
  };

  const tabs = [
    { id: 'reasons', label: 'סיבות היעדרות', icon: AlertCircle },
    { id: 'roles', label: 'תפקידים ודצפלינות', icon: Briefcase },
    { id: 'backup', label: 'הגדרות גיבוי ענן', icon: Cloud },
    { id: 'system', label: 'אבטחה ותחזוקה', icon: Shield },
    { id: 'cache', label: 'ניהול זיכרון מטמון', icon: Database },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8" dir="rtl">
      <div className="flex items-center gap-4 mb-8">
        <div className="p-4 bg-indigo-600 text-white rounded-3xl shadow-lg shadow-indigo-200">
          <SettingsIcon size={32} />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-slate-900">הגדרות מערכת</h2>
          <p className="text-slate-500">ניהול רשימות וערכי ברירת מחדל למסד הנתונים</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50'
            }`}
          >
            <tab.icon size={20} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        {activeTab === 'backup' ? (
          <div className="space-y-6">
            <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100">
              <h3 className="text-lg font-bold text-indigo-900 mb-2 flex items-center gap-2">
                <Cloud size={20} />
                גיבוי אוטומטי לענן חיצוני (Azure/Webhooks)
              </h3>
              <p className="text-indigo-700 text-sm leading-relaxed">
                ניתן להגדיר כתובת URL (Webhook) אליה המערכת תשלח עותק של הגיבוי בכל פעם שמתבצע גיבוי אוטומטי או ידני. 
                זה מאפשר לך לשמור את הנתונים ב-Azure Functions, Google Cloud Functions או כל שרת חיצוני אחר.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 block mr-1">כתובת URL לגיבוי (Webhook URL)</label>
                <div className="flex gap-4">
                  <input
                    type="url"
                    value={remoteUrl}
                    onChange={(e) => setRemoteUrl(e.target.value)}
                    placeholder="https://your-azure-function.azurewebsites.net/api/backup"
                    className="flex-1 px-6 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  />
                  <button
                    onClick={handleSaveRemoteUrl}
                    disabled={savingUrl}
                    className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
                  >
                    {savingUrl ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    <span>שמור הגדרות</span>
                  </button>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-500 space-y-2">
                <p className="font-bold">מידע טכני:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>המערכת תשלח בקשת POST בפורמט JSON.</li>
                  <li>הגוף (Body) יכיל אובייקט עם השדות: timestamp ו-data (כל נתוני המערכת).</li>
                  <li>וודא שהשרת המקבל מאפשר בקשות CORS מהדומיין של האפליקציה.</li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'system' ? (
          <div className="space-y-6">
            <div className="p-6 bg-amber-50 rounded-2xl border border-amber-100">
              <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
                <Shield size={20} />
                אבטחה ותחזוקת מערכת
              </h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                כאן ניתן לנעול את המערכת לצורך תחזוקה. כאשר מצב תחזוקה פעיל, רק מנהלי מערכת יוכלו להיכנס ולבצע פעולות.
              </p>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900">מצב תחזוקה (נעילת מערכת)</h4>
                  <p className="text-sm text-slate-500 max-w-md">
                    הפעל מצב זה כדי למנוע גישה ממשתמשים רגילים בזמן עדכונים.
                  </p>
                </div>
                <button
                  onClick={toggleMaintenanceMode}
                  disabled={savingSystem}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none ${
                    maintenanceMode ? 'bg-amber-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      maintenanceMode ? '-translate-x-7' : '-translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-4">
                <div className="p-3 bg-white text-emerald-600 rounded-xl shadow-sm">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-emerald-900">סטטוס מערכת: תקין</h4>
                  <p className="text-sm text-emerald-700">כל הנתונים מסונכרנים ומגובים ב-Firestore.</p>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'cache' ? (
          <div className="space-y-6">
            <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100">
              <h3 className="text-lg font-bold text-indigo-900 mb-2 flex items-center gap-2">
                <Database size={20} />
                ניהול זיכרון מטמון (Cache Management)
              </h3>
              <p className="text-indigo-700 text-sm leading-relaxed">
                המערכת שומרת נתונים באופן מקומי כדי להפחית את מספר הקריאות לשרת ולשפר את הביצועים. 
                אם אתה נתקל בנתונים לא מעודכנים או בבעיות תצוגה, תוכל לנקות את הזיכרון המטמון.
              </p>
            </div>

            <div className="p-8 bg-white border border-slate-200 rounded-3xl flex flex-col items-center text-center space-y-6">
              <div className="p-4 bg-slate-50 text-slate-400 rounded-full">
                <RefreshCw size={48} className={clearingCache ? 'animate-spin' : ''} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">ניקוי זיכרון מטמון מקומי</h4>
                <p className="text-slate-500 max-w-md mt-2">
                  פעולה זו תמחק את כל הנתונים השמורים בדפדפן ותאלץ את המערכת למשוך נתונים חדשים מ-Firestore.
                </p>
              </div>
              <button
                onClick={handleClearCache}
                disabled={clearingCache}
                className="bg-red-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-100 flex items-center gap-2 disabled:opacity-50"
              >
                {clearingCache ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={20} />}
                <span>נקה זיכרון מטמון ורענן דף</span>
              </button>
            </div>

            <div className="p-8 bg-white border border-slate-200 rounded-3xl flex flex-col items-center text-center space-y-6">
              <div className="p-4 bg-indigo-50 text-indigo-400 rounded-full">
                <Briefcase size={48} className={fixingNurses ? 'animate-spin' : ''} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">תיקון שיוך אחיות</h4>
                <p className="text-slate-500 max-w-md mt-2">
                  מעביר את כל אנשי הצוות שתפקידם "אחות" מרשימת הסייעות לרשימת הצוות החינוכי-טיפולי.
                </p>
              </div>
              <button
                onClick={handleFixNurses}
                disabled={fixingNurses}
                className="bg-indigo-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2 disabled:opacity-50"
              >
                {fixingNurses ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={20} />}
                <span>העבר אחיות לצוות חינוכי</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            {statusMsg && (
              <div className={`p-4 mb-6 rounded-2xl flex items-center justify-between gap-3 text-sm font-bold animate-in fade-in slide-in-from-top-2 ${
                statusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                statusMsg.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                'bg-indigo-50 text-indigo-800 border border-indigo-200'
              }`}>
                <div className="flex items-center gap-2">
                  {statusMsg.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                  <span>{statusMsg.text}</span>
                </div>
                <button 
                  onClick={() => setStatusMsg(null)}
                  className="p-1 hover:bg-black/5 rounded-lg text-xs"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 p-4 bg-slate-50/80 rounded-2xl border border-slate-200/80">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800 text-sm">
                  {activeTab === 'reasons' ? 'סיבות היעדרות מוגדרות במערכת' : 'תפקידים ודצפלינות מוגדרים'}
                </span>
                <span className="bg-indigo-100 text-indigo-700 text-xs px-2.5 py-0.5 rounded-full font-black">
                  {items.length} פריטים
                </span>
              </div>
              <button
                type="button"
                onClick={handleRestoreDefaults}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold hover:bg-indigo-50 transition-all shadow-xs disabled:opacity-50"
                title="טען או שחזר את רשימת ברירת המחדל הרשמית"
              >
                <RotateCcw size={14} className={actionLoading ? 'animate-spin' : ''} />
                <span>שחזר רשימת ברירת מחדל תקנית</span>
              </button>
            </div>

            <form onSubmit={handleAddItem} className="flex gap-4 mb-8">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={`הוסף ${tabs.find(t => t.id === activeTab)?.label.slice(0, -1)} חדש...`}
                className="flex-1 px-6 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm"
              />
              <button
                type="submit"
                disabled={actionLoading || !newItemName.trim()}
                className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
              >
                {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
                <span>הוספה</span>
              </button>
            </form>

            <div className="space-y-3">
              {loading ? (
                <div className="py-12 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="animate-spin mb-4" size={32} />
                  <p>טוען נתונים...</p>
                </div>
              ) : items.length > 0 ? (
                <AnimatePresence>
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-indigo-200 transition-all"
                    >
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-indigo-400" />
                        {editingId === item.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex-1 px-3 py-1.5 rounded-lg border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleUpdateItem(item.id)}
                              className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <span className="font-bold text-slate-700">{item.name}</span>
                        )}
                      </div>
                      {!editingId && (
                        <div className="flex items-center gap-2">
                          {confirmDeleteId === item.id ? (
                            <div className="flex items-center gap-2 bg-red-50 py-1 px-2.5 rounded-xl border border-red-200 animate-in fade-in">
                              <span className="text-xs text-red-700 font-bold">למחוק?</span>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                disabled={actionLoading}
                                className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs"
                              >
                                כן, מחק
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-medium transition-all"
                              >
                                ביטול
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => {
                                  setEditingId(item.id);
                                  setEditName(item.name);
                                }}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                title="עריכה"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(item.id)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                title="מחיקה (עם אישור)"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-slate-400 space-y-4">
                  <List size={40} className="opacity-20" />
                  <p className="font-bold text-slate-600">אין פריטים להצגה ברשימה זו</p>
                  <p className="text-xs text-slate-400 max-w-sm text-center">
                    באפשרותך לטעון את רשימת ברירת המחדל הרשמית המכילה את כל הערכים התקניים בלחיצה אחת:
                  </p>
                  <button
                    onClick={handleRestoreDefaults}
                    disabled={actionLoading}
                    className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-md flex items-center gap-2"
                  >
                    <RotateCcw size={16} />
                    <span>שחזר וטען פריטי ברירת מחדל כעת</span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="bg-amber-50 p-6 rounded-3xl border border-amber-100 flex gap-4">
        <AlertCircle className="text-amber-600 shrink-0" size={24} />
        <div className="text-sm text-amber-800">
          <p className="font-bold mb-1">שים לב:</p>
          <p>מחיקת פריטים מהגדרות אלו לא תמחק נתונים קיימים שכבר משתמשים בערכים אלו, אך הם לא יופיעו יותר כאפשרויות לבחירה בדיווחים חדשים.</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
